import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export function ImageUploader({ images = [], setImages, max = 4 }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = max - images.length;
    if (room <= 0) { toast.error(`Up to ${max} images`); return; }
    setBusy(true);
    const added = [];
    for (const f of files.slice(0, room)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(data.url);
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    }
    setImages([...images, ...added]);
    setBusy(false);
  };

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-2">
        {images.map((url, i) => (
          <div key={url} className="relative w-16 h-16 rounded overflow-hidden border border-white/15" data-testid={`uploaded-image-${i}`}>
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => setImages(images.filter((_, j) => j !== i))}
              data-testid={`remove-image-${i}`}
              className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 text-white hover:text-red-400">
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {images.length < max && (
          <button type="button" onClick={pick} disabled={busy} data-testid="add-image-btn"
            className="w-16 h-16 rounded border border-dashed border-white/25 flex items-center justify-center text-white/50 hover:text-gold hover:border-gold/50 transition-colors">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={onFiles} data-testid="image-file-input" />
    </div>
  );
}

export function PostImages({ images, compact }) {
  if (!images || !images.length) return null;
  if (compact) {
    return (
      <div className="mt-2 flex flex-wrap gap-2" data-testid="post-images">
        {images.map((url) => (
          <a key={url} href={url} target="_blank" rel="noreferrer" className="block w-20 h-20 rounded-md overflow-hidden border border-white/10 bg-white/5 shrink-0">
            <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
          </a>
        ))}
      </div>
    );
  }
  return (
    <div className={`mt-3 grid gap-2 ${images.length === 1 ? "grid-cols-1 max-w-md" : "grid-cols-2 max-w-md"}`} data-testid="post-images">
      {images.map((url, i) => (
        <a key={url} href={url} target="_blank" rel="noreferrer" className="block rounded-md overflow-hidden border border-white/10">
          <img src={url} alt="" className="w-full h-auto object-cover max-h-80" loading="lazy" />
        </a>
      ))}
    </div>
  );
}
